/**
 * Particle burst hook.
 *
 * Spawns floating DOM elements based on the active playful theme's particle config.
 * Particles are absolutely positioned, animated via CSS, and auto-cleaned up.
 */

import { useCallback } from 'react';
import { usePlayful } from './usePlayful';

export function useParticles() {
  const { themeDefinition } = usePlayful();

  const burst = useCallback(
    (x: number, y: number, countOverride?: number) => {
      const config = themeDefinition.particles;
      if (!config.enabled || config.count === 0) return;

      const count = countOverride ?? config.count;

      for (let i = 0; i < count; i++) {
        const el = document.createElement('span');
        el.className = 'playful-particle';
        el.textContent = config.characters[i % config.characters.length];
        el.style.fontSize = `${config.size}px`;
        el.style.left = `${x}px`;
        el.style.top = `${y}px`;

        // Random trajectory
        const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
        const distance = config.spread * (0.5 + Math.random() * 0.5);
        const dx = Math.cos(angle) * distance;
        const dy = Math.sin(angle) * distance - 20; // bias upward

        el.style.setProperty('--dx', `${dx}px`);
        el.style.setProperty('--dy', `${dy}px`);
        el.style.animationDuration = `${config.duration}ms`;

        document.body.appendChild(el);

        // Self-cleanup
        setTimeout(() => el.remove(), config.duration + 50);
      }
    },
    [themeDefinition.particles]
  );

  return { burst };
}
